import { FC, useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { CustomDropdown, CustomTooltip, Loader } from '@components';
import DealSetupFooter from './DealSetupFooter';
import {
  GameDataDetailsPayload,
  useLazyGetGameDataDetailsQuery,
  useUpdateGameDataDetailsMutation,
} from '@api/gameDataDetails';
import { DataAccessProvider, MMP_SOURCE_MAP, TOAST_TYPES } from '@constants/constants';
import { setToastMessage } from '@stores/App/slice';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';

export interface GameDataDetails {
  cohortStartMonth: string;
  cohortEndMonth: string;
  channelSplitAvailable: boolean;
  countrySplitAvailable: boolean;
  platformSplitAvailable: boolean;
  primaryMonetizationSource: string;
  dataSource: DataAccessProvider;
  cohorts: string[];
  benchmarkingEnabled: boolean;
  restrictedCohorts: string[];
  maxTransactionPeriod: string;
}
interface DataDetailsProps {
  gameId: string;
  handleCancelClick: () => void;
  dealUpdatedDetailsComponent: JSX.Element;
}

const DataDetails: FC<DataDetailsProps> = ({
  gameId,
  handleCancelClick,
  dealUpdatedDetailsComponent,
}) => {
  const dispatch = useDispatch();
  const [gameDataDetails, setGameDataDetails] = useState<GameDataDetails | null>(null);
  const [gameDataPayload, setGameDataPayload] = useState<{
    benchmarkingEnabled: boolean | null;
    restrictedCohorts: string[] | null;
  }>({
    benchmarkingEnabled: null,
    restrictedCohorts: null,
  });

  const [getGameDataDetails] = useLazyGetGameDataDetailsQuery();
  const [updateGameDataDetails] = useUpdateGameDataDetailsMutation();

  //Hook to fetch game data details
  useEffect(() => {
    if (gameId) {
      fetchGameDataDetails(gameId);
    }
  }, [gameId]);

  // Function to fetch game data details and initialise payload
  const fetchGameDataDetails = async (id: string) => {
    const res = await getGameDataDetails(id).unwrap();
    if (res?.data) {
      const gameDataDetailsData = res.data;
      setGameDataDetails(gameDataDetailsData);
      setGameDataPayload({
        benchmarkingEnabled: gameDataDetailsData.benchmarkingEnabled,
        restrictedCohorts: gameDataDetailsData.restrictedCohorts,
      });
    }
  };

  // Handle month selection
  const handleMonthSelect = (event: SelectChangeEvent) => {
    const selectedMonth = event.target.value;

    if (
      gameDataPayload?.restrictedCohorts &&
      selectedMonth &&
      !gameDataPayload.restrictedCohorts.includes(selectedMonth)
    ) {
      const updatedGameDataPayload = {
        ...gameDataPayload,
        restrictedCohorts: [...gameDataPayload.restrictedCohorts, selectedMonth],
      };
      setGameDataPayload(updatedGameDataPayload);
    }
  };

  // Handle month removal
  const handleMonthDeselect = (selectedMonth: string) => {
    if (gameDataPayload?.restrictedCohorts) {
      const updatedgameDataPayload = {
        ...gameDataPayload,
        restrictedCohorts: gameDataPayload.restrictedCohorts.filter(
          (month) => month !== selectedMonth,
        ),
      };
      setGameDataPayload(updatedgameDataPayload);
    }
  };

  // Handle benchmarks toggle
  const handleBenchmarksChange = () => {
    if (gameDataPayload?.benchmarkingEnabled !== null) {
      const updatedgameDataPayload = {
        ...gameDataPayload,
        benchmarkingEnabled: !gameDataPayload?.benchmarkingEnabled,
      };
      setGameDataPayload(updatedgameDataPayload);
    }
  };

  // Handle save click
  const handleSaveClick = async () => {
    if (gameDataDetails) {
      const payload: GameDataDetailsPayload = {
        gameId,
        ...(gameDataDetails.benchmarkingEnabled !== gameDataPayload.benchmarkingEnabled &&
          gameDataPayload.benchmarkingEnabled !== null && {
            benchmarkingEnabled: gameDataPayload.benchmarkingEnabled,
          }),
        ...(gameDataDetails.restrictedCohorts !== gameDataPayload.restrictedCohorts &&
          gameDataPayload.restrictedCohorts !== null && {
            restrictedCohorts: gameDataPayload.restrictedCohorts,
          }),
      };

      try {
        await updateGameDataDetails(payload).unwrap();
        handleCancelClick();
      } catch (error: any) {
        dispatch(
          setToastMessage({
            message: 'Failed to update, please try again',
            type: TOAST_TYPES.ERROR,
          }),
        );
      }
    }
  };

  // Check if current or last month
  const isLastToLastMonth = (date: string) => {
    const lastMonth = dayjs().subtract(1, 'month').format('YYYY-MM');
    const currentMonth = dayjs().format('YYYY-MM');
    return date === lastMonth || date === currentMonth;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.25rem',
          height: '100%',
          overflowY: 'auto',
          paddingBottom: '2rem',
        }}
      >
        {gameDataDetails ? (
          <>
            <Box
              sx={{
                backgroundColor: '#fff',
                padding: '1.5rem 1.25rem',
                borderRadius: '1rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                border: '2px solid #2F736E1F',
              }}
            >
              <Typography
                sx={{
                  color: '#162936',
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: '1.375rem',
                }}
              >
                Data Source
              </Typography>
              <Typography
                sx={{
                  color: '#162936',
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  lineHeight: '1rem',
                }}
              >
                {MMP_SOURCE_MAP[gameDataDetails?.dataSource] || '-'}
              </Typography>
            </Box>
            <Box
              sx={{
                backgroundColor: '#fff',
                padding: '1.5rem 1.25rem',
                borderRadius: '1rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                border: '2px solid #2F736E1F',
              }}
            >
              <Typography
                sx={{
                  color: '#162936',
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: '1.375rem',
                }}
              >
                Data Status
              </Typography>
              {gameDataDetails?.maxTransactionPeriod ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'row',
                    gap: '0.5rem',
                    borderBottom: '1px solid #E8EAEB',
                    paddingBottom: '1rem',
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: !isLastToLastMonth(gameDataDetails?.maxTransactionPeriod)
                        ? '#FF6347'
                        : '#2F736E',
                      borderRadius: '0.25rem',
                      width: '0.75rem',
                      height: '0.75rem',
                    }}
                  />
                  <Typography
                    sx={{
                      color: '#000',
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      lineHeight: '1rem',
                    }}
                  >
                    {!isLastToLastMonth(gameDataDetails?.maxTransactionPeriod)
                      ? 'Not Updated'
                      : 'Updated'}{' '}
                    • last transaction month{' '}
                    {dayjs(gameDataDetails?.maxTransactionPeriod).format('MMM YYYY')}
                  </Typography>
                </Box>
              ) : (
                <></>
              )}
              <Box
                sx={{
                  width: '13rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                }}
              >
                <Typography
                  sx={{
                    color: '#162936',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    lineHeight: '1rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  Cohort Start Month{' '}
                  <span style={{ fontWeight: 700 }}>
                    {gameDataDetails?.cohortStartMonth || '-'}
                  </span>
                </Typography>
                <Typography
                  sx={{
                    color: '#162936',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    lineHeight: '1rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  Cohort End Month{' '}
                  <span style={{ fontWeight: 700 }}>{gameDataDetails?.cohortEndMonth || '-'}</span>
                </Typography>
              </Box>
              <Box
                sx={{
                  borderTop: '1px solid #E8EAEB',
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  paddingTop: '1rem',
                }}
              >
                <Typography
                  sx={{
                    color: '#162936',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    lineHeight: '1rem',
                  }}
                >
                  Data split by Channel
                  <CustomTooltip
                    customStyle={{
                      '& .MuiTooltip-tooltip': {
                        borderRadius: '0.25rem',
                        bgcolor: '#00000095',
                        boxShadow: 2,
                        paddingX: '0.75rem',
                        paddingY: '0.5rem',
                        position: 'relative',
                        color: '#F0F0F0F1',
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        lineHeight: '1.25rem',
                      },
                    }}
                    content={
                      gameDataDetails?.platformSplitAvailable ? 'Available' : 'Not Available'
                    }
                  >
                    <Checkbox
                      icon={<RadioButtonUncheckedIcon />}
                      checkedIcon={<CheckCircleOutlineIcon sx={{ color: '#7FBCAD' }} />}
                      checked={gameDataDetails?.channelSplitAvailable}
                      sx={{
                        padding: '0 0 0 0.5rem',
                        cursor: 'default',
                      }}
                    />
                  </CustomTooltip>
                </Typography>
                <Typography
                  sx={{
                    color: '#162936',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    lineHeight: '1rem',
                  }}
                >
                  Data split by Country
                  <CustomTooltip
                    customStyle={{
                      '& .MuiTooltip-tooltip': {
                        borderRadius: '0.25rem',
                        bgcolor: '#00000095',
                        boxShadow: 2,
                        paddingX: '0.75rem',
                        paddingY: '0.5rem',
                        position: 'relative',
                        color: '#F0F0F0F1',
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        lineHeight: '1.25rem',
                      },
                    }}
                    content={
                      gameDataDetails?.platformSplitAvailable ? 'Available' : 'Not Available'
                    }
                  >
                    <Checkbox
                      icon={<RadioButtonUncheckedIcon />}
                      checkedIcon={<CheckCircleOutlineIcon sx={{ color: '#7FBCAD' }} />}
                      checked={gameDataDetails?.countrySplitAvailable}
                      sx={{
                        padding: '0 0 0 0.5rem',
                        cursor: 'default',
                      }}
                    />
                  </CustomTooltip>
                </Typography>
                <Typography
                  sx={{
                    color: '#162936',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    lineHeight: '1rem',
                  }}
                >
                  Data split by Platform
                  <CustomTooltip
                    customStyle={{
                      '& .MuiTooltip-tooltip': {
                        borderRadius: '0.25rem',
                        bgcolor: '#00000095',
                        boxShadow: 2,
                        paddingX: '0.75rem',
                        paddingY: '0.5rem',
                        position: 'relative',
                        color: '#F0F0F0F1',
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        lineHeight: '1.25rem',
                      },
                    }}
                    content={
                      gameDataDetails?.platformSplitAvailable ? 'Available' : 'Not Available'
                    }
                  >
                    <Checkbox
                      icon={<RadioButtonUncheckedIcon />}
                      checkedIcon={<CheckCircleOutlineIcon sx={{ color: '#7FBCAD' }} />}
                      checked={gameDataDetails?.platformSplitAvailable}
                      sx={{
                        padding: '0 0 0 0.5rem',
                        cursor: 'default',
                      }}
                    />
                  </CustomTooltip>
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '1.25rem',
              }}
            >
              <Box
                sx={{
                  borderRadius: '1rem',
                  backgroundColor: '#fff',
                  padding: '1.5rem 1.25rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                  border: '2px solid #2F736E1F',
                  width: '100%',
                }}
              >
                <Typography
                  sx={{
                    color: '#162936',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 700,
                    fontSize: '1rem',
                    lineHeight: '1.375rem',
                  }}
                >
                  Benchmarking Data
                </Typography>
                <FormControlLabel
                  label="Include these Cohorts in Benchmarks"
                  sx={{
                    '& .MuiTypography-root': {
                      color: '#162936',
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      lineHeight: '1rem',
                    },
                    '& .MuiFormControlLabel-root': {
                      marginLeft: '0',
                    },
                  }}
                  control={
                    <Checkbox
                      sx={{ paddingY: '0' }}
                      checked={gameDataPayload?.benchmarkingEnabled ?? false}
                      onChange={handleBenchmarksChange}
                    />
                  }
                />
              </Box>
              <Box
                sx={{
                  borderRadius: '1rem',
                  width: '100%',
                  backgroundColor: '#fff',
                  padding: '1.5rem 1.25rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                  border: '2px solid #2F736E1F',
                }}
              >
                <Typography
                  sx={{
                    color: '#162936',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 700,
                    fontSize: '1rem',
                    lineHeight: '1.375rem',
                  }}
                >
                  Primary Monetisation Source
                </Typography>
                <Typography
                  sx={{
                    color: '#162936',
                    fontFamily: 'Manrope, sans-serif',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    lineHeight: '1rem',
                  }}
                >
                  {gameDataDetails?.primaryMonetizationSource || '-'}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                backgroundColor: '#fff',
                padding: '1.5rem 1.25rem',
                borderRadius: '1rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                border: '2px solid #2F736E1F',
              }}
            >
              <Typography
                sx={{
                  color: '#162936',
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 700,
                  fontSize: '1rem',
                  lineHeight: '1.375rem',
                }}
              >
                Cohort Restriction
              </Typography>
              {gameDataDetails?.cohorts?.length > 0 ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '0.5rem',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#162936',
                      fontFamily: 'Manrope, sans-serif',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      lineHeight: '1rem',
                      width: '8rem',
                      minWidth: '8rem',
                    }}
                  >
                    Remove Cohorts from Underwriting
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <CustomDropdown
                      selectedOption={'add_months'}
                      dropdownOptions={[
                        { id: 'add_months', label: 'Add Months' },
                        ...(gameDataDetails?.cohorts || [])
                          .filter(
                            (month) =>
                              !(gameDataPayload?.restrictedCohorts || [])?.some(
                                (selected) => selected === month,
                              ),
                          )
                          .map((month) => ({ id: month, label: month })),
                      ]}
                      handleChange={handleMonthSelect}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: '15rem',
                          },
                        },
                      }}
                    />
                    {[...(gameDataPayload?.restrictedCohorts || [])]
                      ?.sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
                      ?.map((month) => (
                        <Chip
                          key={month}
                          label={dayjs(month).format('MMM YYYY')}
                          onDelete={() => handleMonthDeselect(month)}
                          sx={{
                            backgroundColor: '#F0F0F1',
                            color: '#6C6C6C',
                            fontWeight: 700,
                            fontSize: '0.875rem',
                            borderRadius: '0.5rem',
                          }}
                        />
                      ))}
                  </Box>
                </Box>
              ) : (
                <>-</>
              )}
            </Box>
          </>
        ) : (
          <Loader />
        )}
      </Box>
      <DealSetupFooter
        dealUpdatedDetailsComponent={dealUpdatedDetailsComponent}
        onCancel={handleCancelClick}
        onSave={handleSaveClick}
        isSubmitEnabled={true}
      />
    </Box>
  );
};

export default DataDetails;
